import React, { useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import { Translation, Locale } from 'locale'
import {
  User,
  AuthorisedUser,
  UserStatus,
  ReferenceDataEntityType,
  ReferenceDataManagementEvent,
  EntityIdAndType,
  EntityType,
  WorkflowActions,
  EntityState,
  EntityTypeEnum,
  Organisation,
  Severity,
  OrganisationType
} from 'api'
import { ModalFooter, ModalWindow, Groupbox } from 'view/components/panels'
import { DialogRow, DialogField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { NotificationType, withModal, ModalConsumerProps } from 'view/shared/modal'
import { Tabs } from 'view/components/tabs'
import { UserDetailsPanel } from './UserDetailsPanel'
import { ProductsPanel } from './ProductsPanel'
import { TradingEntitiesPanel } from './TradingEntitiesPanel'
import { createWorkflowReferenceDataAvailableEventsSelector, createWorkflowReferenceDataEntitySelector } from 'selectors/workflowSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { NotesPanel } from './NotesPanel'
import { TradingLimitsPanel } from 'view/routes/tradingLimits/TradingLimitsGrid/TradingLimitsPanel'
import { createUserByUserIdGetterSelector } from 'selectors/userSelectors'
import { FatFingerUserLimitPanel } from 'view/routes/tradingFatFingerLimits/TradingFatFingerLimitsGrid/FatFingerUserLimitPanel'

export type UserDialogProps = OwnProps & StateProps & DispatchProps

interface OwnProps extends ModalConsumerProps {
  workflowId: string
  userEntityId: string
  onClose?: () => unknown
  readMode: boolean
  getUser?: (userId: string) => User
}

interface StateProps {
  userEntity: ReferenceDataEntityType<User> | null
  availableEvents: ReferenceDataManagementEvent[]
  authorisedUser: AuthorisedUser
  getOrganisation: (organisationId: string) => Organisation | null
}

interface DispatchProps {
  onUpdate: (payload: { updatedEntity: User; workflowId: string } & EntityIdAndType) => unknown
  onSubmitWorkflow: (workflowId: string) => unknown
  onCancelWorkflow: (workflowId: string) => unknown
}

const workflowIdSelector = (_state: {}, ownProps: UserDialogProps) => ownProps.workflowId
const userEntityIdSelector = (_state: {}, ownProps: UserDialogProps) => ownProps.userEntityId

const mapStateToProps = createStructuredSelector({
  userEntity: createWorkflowReferenceDataEntitySelector(EntityType.USER, workflowIdSelector, userEntityIdSelector),
  availableEvents: createWorkflowReferenceDataAvailableEventsSelector(workflowIdSelector),
  getUser: createUserByUserIdGetterSelector,
  authorisedUser: createAuthorisedUserSelector(),
  getOrganisation: createEntityGetterSelector(EntityType.ORGANISATION)
})

const mapDispatchToProps = {
  onUpdate: WorkflowActions.createEntityUpdateReferenceData,
  onSubmitWorkflow: WorkflowActions.createSubmitWorkflow,
  onCancelWorkflow: WorkflowActions.createCancelWorkflow
}

const Content = styled(Groupbox)`
  min-width: 540px;
  min-height: 450px;
  padding: 20px 20px 0 20px;

  & > *:last-child {
    border-bottom: none;
  }
`

export const UserDialogComponent: React.FunctionComponent<UserDialogProps> = props => {
  const {
    workflowId,
    userEntity,
    availableEvents,
    authorisedUser,
    getOrganisation,
    onClose,
    showDialog,
    onUpdate,
    onSubmitWorkflow,
    onCancelWorkflow,
    readMode,
    userEntityId,
    getUser
  } = props

  const [selectedTab, setSelectedTab] = useState<Translation>(Translation.TC_USER_DETAILS)

  let user = userEntity?.data
  if (getUser && readMode) {
    user = getUser(userEntityId)
  }

  const organisation = !isNil(user?.organisationId) ? getOrganisation(user?.organisationId ?? '') : null
  const isNewUser = userEntity?.entityState === EntityState.NEW
  const title = isNewUser ? Locale.translate(Translation.TC_NEW_USER) : `${user?.lastName}, ${user?.firstName}`
  const readOnly = !availableEvents.includes(ReferenceDataManagementEvent.ENTITY_UPDATE)
  const validationIssues = userEntity?.validationIssues ?? []
  const hasValidationIssues = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const handleUpdateUser = (partialUser: Partial<User>) => {
    if (!user) return

    const newUser: User = {
      ...user,
      ...partialUser
    }

    // clear trading entity ids if organisation has changed
    if (partialUser.organisationId && partialUser.organisationId !== user.organisationId) {
      newUser.tradingEntityDetails = []
    }

    onUpdate({
      entityId: userEntity?.id ?? '',
      entityType: EntityTypeEnum.USER,
      updatedEntity: newUser,
      workflowId
    })
  }

  const handleSave = async () => {
    if (!user) return

    if (user.status === UserStatus.DELETED) {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_PROMPT_TITLE_DELETE_USER,
        message: Translation.SC_PROMPT_MSG_DELETE_USER,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }

      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult !== Translation.TC_BUTTON_YES) {
        return
      }
    }

    onSubmitWorkflow(workflowId)
    if (onClose) {
      onClose()
    }
  }

  const handleClose = () => {
    if (workflowId) {
      onCancelWorkflow(workflowId)
    }

    if (onClose) {
      onClose()
    }
  }
  const isBuySideOrganisation = organisation?.organisationTypes.includes(OrganisationType.BUY_SIDE)

  const tabs = [
    Translation.TC_USER_DETAILS,
    Translation.TC_PRODUCTS,
    Translation.TC_TRADING_ENTITIES,
    Translation.TC_USER_LIMITS,
    Translation.TC_FAT_FINGER_LIMITS,
    Translation.TC_NOTES
  ]

  const filteredTabs = !isBuySideOrganisation
    ? tabs.filter(tab => tab !== Translation.TC_USER_LIMITS && tab !== Translation.TC_FAT_FINGER_LIMITS)
    : tabs
  const isReadOnly = readMode || readOnly
  return (
    <ModalWindow title={title}>
      <Tabs dataTestId={'UserDialog'} selectedTab={selectedTab} onChangeTab={setSelectedTab} tabs={filteredTabs} />
      <Content>
        {selectedTab === Translation.TC_USER_DETAILS && user && (
          <UserDetailsPanel
            user={user}
            isNewUser={isNewUser}
            organisation={organisation}
            authorisedUser={authorisedUser}
            validationIssues={validationIssues}
            readOnly={isReadOnly}
            onUpdateUser={handleUpdateUser}
          />
        )}
        {selectedTab === Translation.TC_PRODUCTS && user && (
          <ProductsPanel user={user} readOnly={isReadOnly} onUpdateUser={handleUpdateUser} />
        )}
        {selectedTab === Translation.TC_TRADING_ENTITIES && user && (
          <TradingEntitiesPanel user={user} readOnly={isReadOnly} onUpdateUser={handleUpdateUser} />
        )}
        {selectedTab === Translation.TC_USER_LIMITS && user && isBuySideOrganisation && (
          <TradingLimitsPanel user={user} readOnly={isReadOnly} onUpdateUser={handleUpdateUser} />
        )}
        {selectedTab === Translation.TC_FAT_FINGER_LIMITS && user && isBuySideOrganisation && (
          <FatFingerUserLimitPanel
            readOnly={isReadOnly}
            workflowId={workflowId}
            organisationType={OrganisationType.SELL_SIDE}
            organisationId={organisation?.id || ''}
            userId={user.id}
          />
        )}
        {selectedTab === Translation.TC_NOTES && user && (
          <NotesPanel user={user} validationIssues={validationIssues} readOnly={isReadOnly} onUpdateUser={handleUpdateUser} />
        )}
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton dataTestId={'UserDialog-TextButton-ButtonCancel'} type={TextButtonType.Tertiary} onClick={handleClose}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              dataTestId={'UserDialog-TextButton-ButtonSave'}
              type={TextButtonType.Primary}
              disabled={isReadOnly || hasValidationIssues}
              onClick={handleSave}>
              {Locale.translate(Translation.TC_BUTTON_SAVE)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const UserDialog = withModal(connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(UserDialogComponent))
